import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppSelector } from "../../../hooks/hooks";
import { selectIsAuthenticated } from "../../../auth/data-access/store/authSlice";
import {
  useResolveHeMutation,
  useLoginViaHeMutation,
} from "../../../auth/data-access/store/services/auth.service";

const HYDRA_NO_ACCOUNT_FLAG = "hydra_no_account";

export interface HeLoginStatus {
  isProcessing: boolean;
  error: string | null;
}

/**
 * Automatic login via Header Enrichment (Solution Proposal, Diagram 1).
 *
 * Flow:
 * 1. Skip if already authenticated or a `uuid` URL parameter is present (that
 *    case is owned by useHydraUuidLogin). Attempt once per page load.
 * 2. Call /he/resolve — the backend reads the operator's X-Orange-ISE2 header and
 *    returns an opaque `heToken` (the encrypted MSISDN).
 * 3. If a token is returned, exchange it for a session via /user/login-via-he.
 *    - Success: the authApi matcher flips auth state and routing redirects home.
 *    - "No account": flag the sign-in page to show the subscribe modal.
 *    - No token / other error: fall through to manual (OTP) login.
 *
 * The token is never displayed or decoded on the client.
 */
export function useHeLogin(): HeLoginStatus {
  const [searchParams] = useSearchParams();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const [resolveHe] = useResolveHeMutation();
  const [loginViaHe] = useLoginViaHeMutation();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Guards against duplicate runs within a single page load (re-renders /
  // StrictMode double-invoke). A fresh page load resets it, so HE is retried on
  // every landing rather than being permanently suppressed.
  const hasAttemptedRef = useRef(false);

  useEffect(() => {
    if (isAuthenticated) {
      return;
    }

    // The uuid-parameter auto-login (useHydraUuidLogin) owns that case.
    if (searchParams.has("uuid")) {
      return;
    }

    if (hasAttemptedRef.current) {
      return;
    }
    hasAttemptedRef.current = true;

    const processHe = async () => {
      setIsProcessing(true);

      try {
        const resolved = await resolveHe().unwrap();
        const heToken = resolved?.heToken;

        // No Header Enrichment available (e.g. WiFi) — proceed to manual login.
        if (!heToken) {
          setIsProcessing(false);
          return;
        }

        const loginResult = await loginViaHe({ heToken });

        if ("error" in loginResult) {
          const errorMsg =
            (loginResult.error as { data?: { message?: string } })?.data
              ?.message ?? "";
          const isNoAccount =
            errorMsg.toLowerCase().includes("no account") ||
            errorMsg.toLowerCase().includes("not found");

          if (isNoAccount) {
            // Signal the sign-in page to open the subscribe modal.
            sessionStorage.setItem(HYDRA_NO_ACCOUNT_FLAG, "true");
          }

          setIsProcessing(false);
          navigate("/auth/sign-in", { replace: true });
          return;
        }

        // Success: auth state is updated by the authApi matcher; routing handles
        // the redirect to the home page.
        setIsProcessing(false);
      } catch (err) {
        // Resolve/login failed unexpectedly — fall back to manual login.
        setError(err instanceof Error ? err.message : "Unknown error");
        setIsProcessing(false);
        navigate("/auth/sign-in", { replace: true });
      }
    };

    processHe();
  }, [searchParams, isAuthenticated, resolveHe, loginViaHe, navigate]);

  return {
    isProcessing,
    error,
  };
}
